import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import QuizGame from './QuizGame';
import useRandomWords from './useRandomWords';
import useUser from './useUser';
import useAuth from './useAuth';
import AccountPanel from './AccountPanel';
import useAudio from './useAudio';
import { useLang } from './lang';
import { FREQUENCY_VALUES, FREQUENCY_NAMES, cefrOfBand } from './levels';
import { isTheme } from './themes';
import { loadSession } from './quizSession';
import FilterBar from './FilterBar';
import Header from './Header';
import Footer from './Footer';
import useDailyGoal from './useDailyGoal';
import SessionComplete from './SessionComplete';
import TexteFinal from './TexteFinal';
import LevelTest from './LevelTest';
import ListeningComprehension from './ListeningComprehension';
// Pages PERSONNELLES (propriétaire uniquement). Elles ne partagent rien avec le
// quiz : chacune a ses propres composants et ses propres routes d'API.
import Tutor from './Tutor';
import FormsDrill from './FormsDrill';
import FormsTables from './FormsTables';
import Writing from './Writing';
import Recall from './Recall';
import TutorQuiz from './tuteur/Quiz';
import Decliner from './tuteur/Decliner';
import { authHeaders, jsonAuthHeaders } from './apiAuth';

const App = () => {
  const lang = useLang();

  // Le niveau CECR est accolé au libellé de la bande (« 1 000 mots (A2) »), donc il
  // apparaît d'un coup dans la phrase du filtre ET dans les pastilles du tiroir.
  // Sans lui, « 1 000 mots » ne veut rien dire pour qui débute, et le test de niveau
  // répondait « B1 » quand le filtre parlait en milliers : deux échelles étrangères
  // l'une à l'autre alors qu'elles mesurent la même chose.
  const FREQUENCIES = lang.ui.frequencyLabels.map((label, i) => ({
    label: `${label} (${cefrOfBand(FREQUENCY_VALUES[i])})`,
    value: FREQUENCY_VALUES[i],
    name: FREQUENCY_NAMES[i],
  }));

  const [frequency, setFrequency] = useState(() => {
    try { return parseInt(localStorage.getItem('motrusse_frequency') ?? '500', 10); }
    catch { return 500; }
  });
  const [isLevelTest] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('test'); }
    catch { return false; }
  });
  // Page cachée « compréhension orale » (?ecoute) : réservée aux comptes, non liée
  // depuis le menu. Rendue avant tout le reste, comme le test de niveau.
  const [isListening] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('ecoute'); }
    catch { return false; }
  });
  // Pages personnelles : un seul paramètre à lire, rendu avant tout le reste.
  // Rien de ce qui suit ne modifie le quiz : ces pages sont des écrans à part.
  const [pagePerso] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      return ['tuteur', 'formes', 'tableaux', 'ecrire', 'rappel', 'serie', 'decliner'].find((k) => q.has(k)) || null;
    } catch { return null; }
  });
  // Mot d'atterrissage : une fiche SEO /mot/… renvoie vers /?mot=<id>.
  const [seedWordId] = useState(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('mot');
      if (raw && /^\d+$/.test(raw)) return parseInt(raw, 10);
    } catch {}
    return null;
  });
  // Thème d'ATTERRISSAGE (page-thème SEO → /?theme=<slug>) : pré-sélectionne ce thème.
  const [landingTheme] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('theme');
      return isTheme(t) ? t : null;
    } catch { return null; }
  });
  const landing = seedWordId != null || landingTheme != null;
  useEffect(() => {
    if (landing) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { userId } = useUser();
  const auth = useAuth();
  const { soundEnabled, toggleSound, play, playAuto, whenAudioIdle, stopAudio } = useAudio();

  // Reprise de session (retour même onglet depuis /mot/…) : on relit le filtre-thème.
  const [restoredMeta] = useState(() => {
    if (landing) return null;
    try { return loadSession(lang.apiLang, frequency); } catch { return null; }
  });

  // L'app = UN quiz aléatoire qui respecte les filtres (fréquence + thème). Le thème
  // est un simple filtre : « tous » (null) ou un slug. Plus de « mode chapitre ».
  const [filterTheme, setFilterTheme] = useState(() => restoredMeta?.filterTheme ?? landingTheme ?? null);
  const activeThemes = useMemo(() => (filterTheme ? [filterTheme] : null), [filterTheme]);

  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionTotal, setSessionTotal] = useState(0);
  // Compteurs de progression (bilan de fin de série uniquement) : jours
  // d'entraînement cumulés et marche vers le point. 10 séries = 1 point.
  const [stats, setStats] = useState({ days: 0, couverture: 0, seen: 0 });
  const [horizon, setHorizon] = useState(null);
  const [sessionKey, setSessionKey] = useState(0);
  const [showAccount, setShowAccount] = useState(false);
  const [cursive, setCursive] = useState(() => {
    try { return localStorage.getItem('motrusse_script') === 'cursive'; }
    catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('ru-cursive', cursive);
  }, [cursive]);

  const toggleCursive = () => {
    setCursive((prev) => {
      const next = !prev;
      try { localStorage.setItem('motrusse_script', next ? 'cursive' : 'print'); } catch {}
      return next;
    });
  };

  const {
    done: dailyDone,
    rescued: dailyRescued,
    bump: dailyBump,
    rescue: dailyRescue,
    dismiss: dailyDismiss,
  } = useDailyGoal({
    done: restoredMeta?.seriesDone,
    rescued: restoredMeta?.seriesRescued,
  });

  const {
    words,
    fetchWords,
    isLoading,
    error,
    lastIncorrectIds,
    setLastIncorrectIds,
    lancerSerie,
    finirSerie,
    demanderTexteFinal,
  } = useRandomWords(frequency, userId, lang.apiLang, seedWordId, activeThemes, landing);

  // Série de 10 questions : au bout de 10, on affiche le bilan puis on enchaîne.
  //
  // Série IMPOSÉE : une liste de mots choisie dans le panneau de compte
  // (« à revoir », « en erreur », « appris »). Elle ne dure QU'UNE série ; la
  // suivante repasse par le tirage normal.
  const [serieImposee, setSerieImposee] = useState(false);

  const SERIES = 10;
  // Vivier RESTREINT : sur un thème, `words` = tous les mots du thème ∩ bande de
  // fréquence (ex. « nourriture » ∩ top 500 = 2 mots) ; sur une série imposée,
  // c'est la liste demandée, qui peut n'en compter que trois. On borne alors la
  // série au nombre de mots DISTINCTS disponibles, sinon on reposait 10 questions
  // en répétant les mêmes deux mots cinq fois chacun.
  const vivierBorne = Boolean(filterTheme) || serieImposee;
  const poolSize = vivierBorne ? new Set(words.map((w) => w.id)).size : 0;
  const seriesLen = poolSize ? Math.max(1, Math.min(SERIES, poolSize)) : SERIES;
  const seriesComplete = dailyDone >= seriesLen;

  // Toute réponse du back qui porte des compteurs les rafraîchit (/api/stats au
  // montage, /api/progress à chaque réponse, /api/series_done en fin de série).
  const applyStats = useCallback((d) => {
    if (!d) return;
    setStats((s) => ({
      days: d.days ?? s.days,
      couverture: d.couverture ?? s.couverture,
      seen: d.seen ?? s.seen,
    }));
    if (d.horizon) setHorizon(d.horizon);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetch(`/api/stats/${userId}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(applyStats)
      .catch(() => {});
  }, [userId, applyStats]);

  // Série terminée → +1 au compteur (10 séries = 1 point). Postée UNE SEULE fois
  // par série : la ref persiste au double-invoke des effets en StrictMode, là où un
  // drapeau remis à zéro dans un cleanup laisserait passer un second envoi (et
  // offrirait une série gratuite à chaque bilan, en développement comme après un
  // simple re-rendu). Elle se réarme quand le bilan se referme.
  const seriesCountedRef = useRef(false);
  useEffect(() => {
    if (!seriesComplete) { seriesCountedRef.current = false; return; }
    if (seriesCountedRef.current || !userId) return;
    seriesCountedRef.current = true;
    fetch('/api/series_done', {
      method: 'POST',
      headers: jsonAuthHeaders(),
      body: JSON.stringify({ user_id: userId }),
    })
      .then((r) => r.json())
      .then(applyStats)
      .catch(() => {});
  }, [seriesComplete, userId, applyStats]);


  // ── Texte de fin de série ──────────────────────────────────────────────────
  // Les mots RÉELLEMENT répondus pendant la série. C'est la seule liste qui dise
  // ce que la personne vient de travailler : `words` ne le dit pas, parce que le
  // quiz retire un lot frais après chaque réponse et n'en pose qu'une carte.
  //
  // En état plutôt qu'en ref : la liste voyage dans la séance sauvegardée, donc
  // elle doit provoquer un rendu pour que QuizGame la réenregistre.
  const [motsRepondus, setMotsRepondus] = useState(restoredMeta?.motsRepondus ?? []);

  const [texte, setTexte] = useState(null);
  // La demande de texte de fin est-elle RÉGLÉE (arrivée, vide, ou expirée) ?
  //
  // Sans ce drapeau, la série se terminait sur un ÉCLAIR DE BILAN : `texte` vaut
  // null tant que la réponse n'est pas là, donc `seriesComplete && !texte`
  // affichait le bilan, puis le texte le remplaçait. Mesuré au navigateur :
  // « BILAN 2/10 » pendant 25 ms, puis le texte. En local — ailleurs, c'est le
  // temps d'un aller-retour.
  //
  // Tant que la demande n'est pas réglée, on ne montre NI l'un NI l'autre : la
  // carte qu'on vient de répondre reste à l'écran, feedback compris. C'est le seul
  // écran qui ne ment sur rien à ce moment-là — la même règle que pour le bilan
  // pendant le tirage du lot suivant.
  //
  // ET IL PART À FAUX, ce qui est tout le sujet. Un drapeau « en vol » posé à VRAI
  // par l'effet arrive trop tard : l'effet ne tourne qu'APRÈS le rendu qui a vu
  // `seriesComplete` passer à vrai, et ce rendu-là affichait déjà le bilan.
  // Mesuré : 7 ms de bilan avant que le drapeau ne le masque. « Pas encore réglé »
  // est le bon état de départ, et il est vrai.
  const [texteRegle, setTexteRegle] = useState(false);
  // On compare l'état PRÉCÉDENT : vider la liste dès que `seriesComplete` est
  // faux la viderait aussi au montage, et une séance reprise perdrait les mots
  // répondus avant le détour par le dictionnaire.
  const prevCompleteRef = useRef(seriesComplete);
  useEffect(() => {
    const avant = prevCompleteRef.current;
    prevCompleteRef.current = seriesComplete;
    if (!seriesComplete) {
      setTexte(null);
      setTexteRegle(false);
      if (avant) setMotsRepondus([]);   // série terminée : ardoise vierge
      return;
    }
    let annule = false;
    (async () => {
      // Course contre une seconde : une requête qui n'aboutit jamais ne doit pas
      // laisser la personne bloquée sur la carte qu'elle vient de répondre. Au
      // pire, le bilan s'affiche seul — c'est déjà le repli prévu quand aucun
      // texte ne convient.
      const t = await Promise.race([
        demanderTexteFinal(motsRepondus),
        new Promise((r) => setTimeout(() => r(null), 1000)),
      ]);
      if (annule) return;
      // `setTexte` AVANT `setTexteRegle` : React 18 regroupe les deux dans un seul
      // rendu, mais l'ordre garantit que même sans regroupement la tuile qui
      // s'affiche est la bonne, jamais le bilan une image avant le texte.
      if (t) setTexte(t);
      setTexteRegle(true);
      if (!t) return;
      try {
        const vus = JSON.parse(localStorage.getItem('textesVus') || '[]');
        localStorage.setItem('textesVus', JSON.stringify([...vus, t.id].slice(-200)));
      } catch { /* quota plein : on reverra un texte, ce n'est pas grave */ }
    })();
    return () => { annule = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesComplete]);

  // La série est finie ET la tuile qui la clôt est prête à s'afficher. Les deux
  // conditions vont ensemble : c'est ce qui empêche le bilan d'apparaître une
  // fraction de seconde à la place du texte qui n'est pas encore arrivé.
  const finDeSerie = seriesComplete && texteRegle;

  // Réinitialise les stats de la séance en cours lors d'un changement de filtre.
  // Une séance neuve est NORMALE par défaut : changer de fréquence ou de thème
  // pendant une série imposée en sort, sinon l'indicateur restait allumé pour
  // des mots qui n'avaient plus rien d'imposé.
  const bumpSession = () => {
    setSessionKey((k) => k + 1);
    setSessionCorrect(0);
    setSessionTotal(0);
    setLastIncorrectIds([]);
    setSerieImposee(false);
    // Les mots répondus repartent à zéro EUX AUSSI : ils servent à choisir le
    // texte de fin, et ceux de la série qu'on vient d'abandonner n'ont plus rien
    // à y faire. Constaté au navigateur avant correction : après un passage de
    // la bande 500 à la bande 2 000, « не » et « что » travaillés AVANT le
    // changement se retrouvaient surlignés dans le texte de la série suivante.
    setMotsRepondus([]);
    dailyDismiss(); // nouveau filtre → nouvelle série
  };

  // Lancer une série sur une liste de mots choisie dans le panneau de compte.
  // On ferme le panneau, on repart d'une séance vierge, et le hook impose les
  // mots. Le filtre de fréquence et le thème ne bougent PAS : ils reprendront la
  // main d'eux-mêmes à la série suivante.
  //
  // `setSerieImposee(true)` vient APRÈS `bumpSession()`, qui le remet à faux :
  // dans l'autre ordre, la remise à zéro de la séance annulait l'imposition.
  const lancerSerieDeMots = (ids) => {
    setShowAccount(false);
    bumpSession();
    setSerieImposee(true);
    lancerSerie(ids);
  };

  const handleFrequencyChange = (value) => {
    setFrequency(value);
    try { localStorage.setItem('motrusse_frequency', value); } catch {}
    setLastIncorrectIds([]);
  };

  const handlePickFrequency = (value) => {
    if (value === frequency) return;
    bumpSession();
    handleFrequencyChange(value);
  };

  const handlePickFilterTheme = (slug) => {
    if (slug === filterTheme) return;
    setFilterTheme(slug);
    bumpSession();
  };

  if (isLevelTest) {
    return <LevelTest play={play} playAuto={playAuto} soundEnabled={soundEnabled} toggleSound={toggleSound} cursive={cursive} toggleCursive={toggleCursive} userId={userId} />;
  }

  if (isListening) {
    return <ListeningComprehension play={play} soundEnabled={soundEnabled} toggleSound={toggleSound} cursive={cursive} toggleCursive={toggleCursive} />;
  }

  if (pagePerso) {
    const Page = { tuteur: Tutor, formes: FormsDrill, tableaux: FormsTables,
                   ecrire: Writing, rappel: Recall, serie: TutorQuiz,
                   decliner: Decliner }[pagePerso];
    return <Page cursive={cursive} toggleCursive={toggleCursive} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      <div className="max-w-xl md:max-w-2xl mx-auto px-4 py-6">

        <header className="mb-5 md:mb-7">
          <Header lang={lang} cursive={cursive} toggleCursive={toggleCursive} />
          <div className="mt-4">
            <FilterBar
              frequencies={FREQUENCIES}
              frequency={frequency}
              onPickFrequency={handlePickFrequency}
              theme={filterTheme}
              onPickTheme={handlePickFilterTheme}
              apiLang={lang.apiLang}
              allLabel={lang.ui.fullQuiz}
              titlePrefix={lang.ui.titlePrefix}
              titleSuffix={lang.ui.titleSuffix}
            />
          </div>
        </header>

        <main>
          {/* Bilan de série EN LIGNE : le quiz reste monté (juste masqué, donc gelé)
              pour reprendre la série suivante sans tout recharger, et header/footer
              restent visibles autour. */}
          {finDeSerie && texte && (
            <TexteFinal
              // Le composant garde un état local (le choix fait, la trace déjà
              // envoyée) : la clé garantit qu'un nouveau texte repart d'une
              // ardoise vierge même si React réutilisait la position.
              key={texte.id}
              texte={texte}
              lang={lang}
              userId={userId}
              play={play}
              stopAudio={stopAudio}
              onDone={() => setTexte(null)}
            />
          )}
          {finDeSerie && !texte && (
            <SessionComplete
              rescued={dailyRescued}
              done={dailyDone}
              correct={sessionCorrect}
              total={sessionTotal}
              horizon={horizon}
              lang={lang}
              invite={!auth.account}
              onOpenAccount={() => setShowAccount(true)}
              onContinue={async () => {
                // On ATTEND le lot suivant avant de rendre la main au quiz.
                // `dailyDismiss()` démasque la carte : appelé tout de suite, il
                // la démasquait sur le mot de la série qu'on vient de finir, le
                // temps que le nouveau lot arrive. D'où le bref éclair.
                // Le bilan reste affiché pendant ce temps (~150 ms) : c'est le
                // seul écran qui ne ment sur rien à ce moment-là.
                //
                // `finirSerie` rend aussi la main au tirage normal : sans lui, la
                // première carte de la série suivante serait encore prise dans
                // les mots d'une révision imposée.
                //
                // Et les compteurs de la séance ne repartent à zéro qu'ICI, avec
                // le reste. Remis à zéro AVANT l'attente, ils vidaient l'anneau
                // du bilan encore affiché : « 0/0 » pendant tout le tirage —
                // mesuré à 35 ms en local, le temps d'un aller-retour ailleurs.
                // C'est ce qui se lisait comme un second bilan à zéro.
                await finirSerie();
                setSessionCorrect(0);
                setSessionTotal(0);
                setSerieImposee(false);
                dailyDismiss();
              }}
            />
          )}
          <div className={finDeSerie ? 'hidden' : ''}>
            <QuizGame
              words={words}
              fetchWords={fetchWords}
              activeFilter={frequency}
              lastIncorrectIds={lastIncorrectIds}
              setLastIncorrectIds={setLastIncorrectIds}
              isLoading={isLoading}
              error={error}
              userId={userId}
              seedWordId={seedWordId}
              themes={activeThemes}
              chapterSlug={null}
              explore={true}
              filterTheme={filterTheme}
              landing={landing}
              onStatsUpdate={applyStats}
              sessionKey={sessionKey}
              soundEnabled={soundEnabled}
              toggleSound={toggleSound}
              play={play}
              playAuto={playAuto}
              whenAudioIdle={whenAudioIdle}
              stopAudio={stopAudio}
              onAnswered={(correct, wordId) => {
                dailyBump();
                // Le mot répondu rejoint la liste qui servira à choisir le texte
                // de fin. Dédoublonné : le quiz peut reposer le même mot dans une
                // série au vivier restreint.
                if (wordId) {
                  setMotsRepondus((l) => (l.includes(wordId) ? l : [...l, wordId]));
                }
                setSessionTotal((t) => t + 1);
                if (correct) setSessionCorrect((c) => c + 1);
              }}
              onRescued={dailyRescue}
              frozen={seriesComplete}
              seriesDone={dailyDone}
              seriesTotal={seriesLen}
              seriesRescued={dailyRescued}
              motsRepondus={motsRepondus}
            />
          </div>

          {stats.couverture > 0 && (
            <p className="mt-6 text-center text-xs text-slate-500 tabular-nums">
              {lang.ui.couverture(stats.couverture)}
            </p>
          )}
        </main>

        <Footer lang={lang}>
          {userId && (
            <button onClick={() => setShowAccount(true)} className="text-xs text-slate-600 hover:text-slate-400 transition-colors flex items-center justify-center gap-1 mx-auto">
              <svg viewBox="0 0 20 20" className="w-3 h-3 fill-current shrink-0"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/></svg>
              {auth.account ? lang.ui.account.loggedInAs(auth.account.handle) : lang.ui.progressSaved}
            </button>
          )}
        </Footer>
      </div>

      {showAccount && userId && (
        <AccountPanel
          auth={auth}
          userId={userId}
          stats={stats}
          apiLang={lang.apiLang}
          onStartSerie={lancerSerieDeMots}
          onClose={() => setShowAccount(false)}
        />
      )}
    </div>
  );
};

export default App;
